import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { listMynewsdeskItems, MYNEWSDESK_TYPE_BY_CATEGORY, MYNEWSDESK_TYPES } from "@/lib/mynewsdesk";

const LOCALE_MAP: Record<string, string> = { en: 'en-US', sv: 'sv-SE', ja: 'ja-JP', zh: 'zh-CN' };

const CATEGORY_MAP: Record<string, { slug: string }> = {
  "blog-post": { slug: "blog-post" },
  "news": { slug: "news" },
  "press-release": { slug: "pressrelease" },
};

const InsightCategory = () => {
  const { t, i18n } = useTranslation('blog');
  const { category } = useParams<{ category: string }>();
  const categoryInfo = CATEGORY_MAP[category || ""];
  const categoryLabel = category ? t(`category.labels.${category}`, { defaultValue: category }) : "";
  const dateLocale = LOCALE_MAP[i18n.language] || 'en-US';

  const mndType = MYNEWSDESK_TYPE_BY_CATEGORY[category || ""];
  const isPressRelease = category === "press-release";

  const pressQuery = useQuery({
    queryKey: ["mynewsdesk-items", mndType],
    enabled: !!mndType,
    staleTime: 10 * 60 * 1000,
    retry: 1,
    queryFn: () => listMynewsdeskItems(mndType, 50),
  });

  const dbQuery = useQuery({
    queryKey: ["insight-category-v2", category],
    enabled: !!categoryInfo && !isPressRelease,
    staleTime: 10 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_categories")
        .select("slug, blog_post_categories(blog_posts(id, title, slug, excerpt, content, featured_image, published_at, status))")
        .eq("slug", categoryInfo.slug)
        .maybeSingle();
      if (error) throw error;
      const list = ((data?.blog_post_categories || []) as any[])
        .map((bpc: any) => bpc.blog_posts)
        .filter((p: any) => p && p.status === "published" && (!p.published_at || new Date(p.published_at) <= new Date()))
        .sort((a: any, b: any) => (b.published_at || "").localeCompare(a.published_at || ""));
      return list;
    },
  });

  const isLoading = pressQuery.isLoading || (!isPressRelease && dbQuery.isLoading);
  const isFetching = pressQuery.isFetching || dbQuery.isFetching;
  const isError = isPressRelease ? pressQuery.isError : pressQuery.isError && dbQuery.isError;
  const refetch = () => { pressQuery.refetch(); if (!isPressRelease) dbQuery.refetch(); };

  // Mynewsdesk publications, mapped to this section by their type_of_media.
  const mndPosts = (pressQuery.data || []).map((r) => ({
    id: `mnd-${r.id}`,
    title: r.title,
    excerpt: r.summary,
    content: "",
    featured_image: r.image || r.imageThumb,
    published_at: r.publishedAt,
    __href: `/press/${r.slug}`,
  }));

  // Titles published in Mynewsdesk under any other type must not appear here:
  // the Mynewsdesk publication type decides the section.
  const otherTypesQuery = useQuery({
    queryKey: ["mynewsdesk-other-titles", mndType],
    enabled: !!mndType && !isPressRelease,
    staleTime: 10 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const lists = await Promise.all(
        MYNEWSDESK_TYPES.filter((tp) => tp !== mndType).map((tp) => listMynewsdeskItems(tp, 50).catch(() => [])),
      );
      return lists.flat().map((r) => r.title);
    },
  });

  const norm = (v: string) => (v || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  const mndTitles = new Set(mndPosts.map((p) => norm(p.title)));
  const foreignTitles = new Set((otherTypesQuery.data || []).map(norm));

  const posts = (isPressRelease
    ? mndPosts
    : [...mndPosts, ...((dbQuery.data || []) as any[]).filter((p: any) => !mndTitles.has(norm(p.title)) && !foreignTitles.has(norm(p.title)))]
  ).sort((a: any, b: any) => new Date(b.published_at || 0).getTime() - new Date(a.published_at || 0).getTime());

  const getExcerpt = (post: any) => {
    if (post.excerpt) return post.excerpt;
    if (post.content) { const stripped = post.content.replace(/<[^>]*>/g, ""); return stripped.substring(0, 200) + (stripped.length > 200 ? "..." : ""); }
    return "";
  };

  const formatDate = (date: string) => new Date(date).toLocaleDateString(dateLocale, { year: "numeric", month: "long", day: "numeric" });

  if (!categoryInfo) {
    return (
      <>
        <Header />
        <main className="min-h-screen pt-24 pb-16 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold mb-4">{t('category.notFound')}</h1>
            <Link to="/news" className="text-primary hover:underline">{t('category.backToInsight')}</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <html lang={i18n.language} />
        <title>{categoryLabel} | Insight | Cetasol</title>
        <meta name="description" content={`Browse ${categoryLabel.toLowerCase()} articles from Cetasol covering maritime sustainability, vessel performance, and fleet innovation.`} />
        <link rel="canonical" href={`https://cetasol.com/insight/${category}`} />
        <meta property="og:title" content={`${categoryLabel} | Insight | Cetasol`} />
        <meta property="og:description" content={`Browse ${categoryLabel.toLowerCase()} articles from Cetasol.`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://cetasol.com/insight/${category}`} />
        <meta property="og:image" content="https://cetasol.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${categoryLabel} | Insight | Cetasol`} />
        <meta name="twitter:description" content={`Browse ${categoryLabel.toLowerCase()} articles from Cetasol.`} />
        <meta name="twitter:image" content="https://cetasol.com/og-image.jpg" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org", "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://cetasol.com/" },
            { "@type": "ListItem", "position": 2, "name": "Insight", "item": "https://cetasol.com/news" },
            { "@type": "ListItem", "position": 3, "name": categoryLabel, "item": `https://cetasol.com/insight/${category}` }
          ]
        })}</script>
      </Helmet>
      <Header />
      <main className="min-h-screen pt-24 pb-16 section-fade">
        <div className="container mx-auto px-6">
          <Link to="/news" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8">
            <ArrowLeft size={16} /> {t('category.backToInsight')}
          </Link>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">{categoryLabel}</h1>
          <p className="text-muted-foreground text-lg mb-10">{t('category.allArticles', { category: categoryLabel.toLowerCase() })}</p>

          {isError ? (
            <div className="py-16 text-center">
              <p className="text-muted-foreground mb-4">{i18n.language === 'sv' ? 'Kunde inte ladda innehållet. Försök igen om en stund.' : 'Could not load content. Please try again in a moment.'}</p>
              <button onClick={() => refetch()} disabled={isFetching} className="inline-flex items-center gap-2 px-5 py-2 text-sm font-medium text-primary border border-primary hover:bg-primary hover:text-primary-foreground transition-colors disabled:opacity-50">
                {isFetching ? (i18n.language === 'sv' ? 'Laddar…' : 'Loading…') : (i18n.language === 'sv' ? 'Försök igen' : 'Try again')}
              </button>
            </div>
          ) : isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/3] bg-card mb-4" />
                  <div className="h-4 bg-card rounded w-1/3 mb-2" />
                  <div className="h-3 bg-card rounded w-full mb-1" />
                  <div className="h-3 bg-card rounded w-2/3" />
                </div>
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-12">
              {posts?.map((post) => (
                <Link key={post.id} to={(post as any).__href || `/${post.slug}`} className="group flex flex-col h-full">
                  <div className="relative aspect-[4/3] overflow-hidden mb-4">
                    {post.featured_image ? (<img src={post.featured_image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />) : (<div className="w-full h-full bg-card" />)}
                  </div>
                  <p className="text-xs text-muted-foreground mb-1">{formatDate(post.published_at || "")}</p>
                  <h2 className="font-display font-bold text-foreground text-lg tracking-wide mb-2 line-clamp-2 min-h-[3.5rem]">{post.title}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed mb-3 flex-1 line-clamp-3">{getExcerpt(post)}</p>

                  <span className="text-sm font-medium text-primary group-hover:underline inline-flex items-center gap-1 mt-auto">{t('category.readMore')} <ArrowRight size={14} /></span>
                </Link>
              ))}
            </div>
          )}

          {posts?.length === 0 && !isLoading && (
            <p className="text-muted-foreground text-center py-16">{t('category.noPosts')}</p>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
};

export default InsightCategory;
